import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import api from '@/utils/api';
import { 
  Plus, Search, MoreHorizontal, Edit, Trash2, Eye, Copy, 
  ArrowLeft, FileText, Globe, Clock, Loader2, ExternalLink
} from 'lucide-react';

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AdminCMS = () => {
  const navigate = useNavigate();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [pageToDelete, setPageToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    checkSessionAndLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkSessionAndLoad = async () => {
    try {
      await api.get(`${API_BASE}/admin/me`, { withCredentials: true });
      loadPages();
    } catch (error) {
      console.error('Session check failed:', error);
      navigate('/admin/login');
    }
  };

  const loadPages = async () => {
    try {
      setLoading(true);
      const response = await api.get(`${API_BASE}/admin/cms/pages`, {
        withCredentials: true
      });
      setPages(response.data || []);
    } catch (error) {
      console.error('Error loading pages:', error);
      toast.error('Erreur lors du chargement des pages');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!pageToDelete) return;
    
    setDeleting(true);
    try {
      await api.delete(`${API_BASE}/admin/cms/pages/${pageToDelete.id}`, {
        withCredentials: true
      });
      toast.success('Page supprimée');
      loadPages();
    } catch (error) {
      console.error('Error deleting page:', error);
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setPageToDelete(null);
    }
  };

  const handleDuplicate = async (page) => {
    try {
      await api.post(`${API_BASE}/admin/cms/pages/${page.id}/duplicate`, {}, {
        withCredentials: true
      });
      toast.success('Page dupliquée');
      loadPages();
    } catch (error) {
      console.error('Error duplicating page:', error);
      toast.error('Erreur lors de la duplication');
    }
  };

  const filteredPages = pages.filter(page => 
    page.meta_title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    page.slug.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('fr-CH', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getPageTypeLabel = (type) => {
    const types = {
      landing: 'Landing Page',
      service: 'Service',
      promo: 'Promotion',
      article: 'Article'
    };
    return types[type] || type;
  };

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/admin/leads')}
            data-testid="back-to-leads-btn"
          >
            <ArrowLeft className="mr-2 h-4 w-4" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold">Gestion des Pages CMS</h1>
            <p className="text-muted-foreground">Créez et gérez vos pages dynamiques</p>
          </div>
        </div>
        <Button 
          onClick={() => navigate('/admin/cms/new')}
          style={{ backgroundColor: 'var(--cta-blue)' }}
          data-testid="create-page-btn"
        >
          <Plus className="mr-2 h-4 w-4" />
          Nouvelle page
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-blue-100 rounded-lg">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pages.length}</p>
                <p className="text-sm text-muted-foreground">Pages totales</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-green-100 rounded-lg">
                <Globe className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pages.filter(p => p.is_published).length}</p>
                <p className="text-sm text-muted-foreground">Publiées</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-orange-100 rounded-lg">
                <Clock className="h-6 w-6 text-orange-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{pages.filter(p => !p.is_published).length}</p>
                <p className="text-sm text-muted-foreground">Brouillons</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search & Table */}
      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <CardTitle>Toutes les pages</CardTitle>
              <CardDescription>
                {filteredPages.length} page{filteredPages.length > 1 ? 's' : ''}
              </CardDescription>
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
                data-testid="search-pages-input"
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : filteredPages.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-lg font-medium">Aucune page trouvée</p>
              <p className="text-muted-foreground mb-4">
                {searchTerm ? 'Modifiez votre recherche' : 'Créez votre première page CMS'}
              </p>
              {!searchTerm && (
                <Button onClick={() => navigate('/admin/cms/new')}>
                  <Plus className="mr-2 h-4 w-4" />
                  Créer une page
                </Button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Titre</TableHead>
                    <TableHead>Slug</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Modifié</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPages.map((page) => (
                    <TableRow key={page.id} data-testid={`page-row-${page.id}`}>
                      <TableCell className="font-medium max-w-[200px] truncate">
                        {page.meta_title}
                      </TableCell>
                      <TableCell className="font-mono text-sm">
                        /{page.slug}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline">
                          {getPageTypeLabel(page.page_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {page.is_published ? (
                          <Badge className="bg-green-100 text-green-800">
                            Publiée
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            Brouillon
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-muted-foreground text-sm">
                        {formatDate(page.updated_at)}
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="sm">
                              <MoreHorizontal className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => navigate(`/admin/cms/edit/${page.id}`)}>
                              <Edit className="mr-2 h-4 w-4" />
                              Modifier
                            </DropdownMenuItem>
                            {page.is_published && (
                              <DropdownMenuItem 
                                onClick={() => window.open(`/p/${page.slug}`, '_blank')}
                              >
                                <ExternalLink className="mr-2 h-4 w-4" />
                                Voir la page
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem onClick={() => handleDuplicate(page)}>
                              <Copy className="mr-2 h-4 w-4" />
                              Dupliquer
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              className="text-red-600"
                              onClick={() => {
                                setPageToDelete(page);
                                setDeleteDialogOpen(true);
                              }}
                            >
                              <Trash2 className="mr-2 h-4 w-4" />
                              Supprimer
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cette page ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La page "{pageToDelete?.meta_title}" sera définitivement supprimée.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              disabled={deleting}
              className="bg-red-600 hover:bg-red-700"
            >
              {deleting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
